import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslocoService, getBrowserLang } from '@ngneat/transloco';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { Chart } from 'chart.js';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './auth/auth.service';
import { animateFirstDataRenderOnlyPlugin } from './data-visualization/chart-animation.plugin';
import { narrowChartAspectRatioPlugin } from './data-visualization/chart-narrow-aspect.plugin';
import { resizeDelayGuardPlugin } from './data-visualization/chart-resize-delay.plugin';
import { RemountOnLanguageChangeDirective } from './directives/remount-on-language-change.directive';
import { httpInterceptorProviders } from './interceptors';
import { LayoutsModule } from './layouts/layouts.module';
import { AccountService } from './services/account.service';
import { ExchangeRateService } from './services/exchange-rate.service';
import { TranslocoMatPaginatorIntlService } from './services/transloco-mat-paginator-intl.service';
import { ThemeContainerComponent } from './theme-container/theme-container.component';
import { TranslocoRootModule } from './transloco-root.module';
import { Language } from './types/language';

export function preloadTransloco(
  accountService: AccountService,
  authService: AuthService,
  transloco: TranslocoService,
) {
  return function () {
    const lang = getBrowserLang() || Language.English;
    const setActiveLangAndLoad = (lang: string) => {
      transloco.setActiveLang(lang);
      return transloco.load(lang).toPromise();
    };

    if (authService.isAuthenticated()) {
      return accountService
        .getUserPreferences()
        .toPromise()
        .then((res) => {
          const language = res?.language || lang;
          return setActiveLangAndLoad(language);
        })
        .catch(() => setActiveLangAndLoad(lang));
    }

    return setActiveLangAndLoad(lang);
  };
}

export function preloadCurrencyRates(authService: AuthService, exchangeRateService: ExchangeRateService) {
  return function () {
    if (authService.isAuthenticated()) {
      return exchangeRateService.loadCurrencyRates()?.toPromise();
    }

    return undefined;
  };
}

export function oAuthStorageFactory(): OAuthStorage {
  return sessionStorage;
}

Chart.defaults.resizeDelay = 200;
Chart.register(resizeDelayGuardPlugin);

const baseAnimation = typeof Chart.defaults.animation === 'object' ? Chart.defaults.animation : {};
Chart.defaults.animation = {
  ...baseAnimation,
  delay: (context) => {
    const { type, mode, dataIndex, chart } = context as {
      type?: string;
      mode?: string;
      dataIndex?: number;
      chart?: Chart;
    };
    if (type !== 'data' || mode !== 'default' || dataIndex === undefined) {
      return 0;
    }
    const count = chart?.data.labels?.length || chart?.data.datasets[0]?.data?.length || 0;
    return count > 1 ? dataIndex * Math.min(50, 600 / count) : 0;
  },
};

Chart.register(animateFirstDataRenderOnlyPlugin);
Chart.register(narrowChartAspectRatioPlugin);

@NgModule({
  declarations: [AppComponent, ThemeContainerComponent, RemountOnLanguageChangeDirective],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiDomain],
        sendAccessToken: false,
      },
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    LayoutsModule,
    FlexLayoutModule,
    NgxStripeModule.forRoot(environment.stripePublishableKey),
    TranslocoRootModule,
  ],
  providers: [
    provideCharts(withDefaultRegisterables()),
    httpInterceptorProviders,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
      },
    },
    {
      provide: MatPaginatorIntl,
      useClass: TranslocoMatPaginatorIntlService,
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: 25,
        pageSizeOptions: [25, 50, 100],
        showFirstLastButtons: true,
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadTransloco,
      deps: [AccountService, AuthService, TranslocoService],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadCurrencyRates,
      deps: [AuthService, ExchangeRateService],
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: OAuthStorage, useFactory: oAuthStorageFactory },
  ],
})
export class AppModule {}
